import { useSelector } from "react-redux";
import { usePathname } from "next/navigation";
import { useState, useRef, useEffect } from "react";

import { useAuth } from "@stadium/services/auth/client";

import AppGenericAnnouncementBar from "@stadium/ui/src/components/app-generic-announcement-bar";
import CartIcon from "@components/store/navigation/cart-icon";
import NavigationRightSection from "@components/store/navigation/right-section";
import BrandLogo from "@components/store/navigation/brand-logo";
import TalkToSalesButton from "@components/talk-to-sales-button";
import GlobalProductSearch from "@sharedComponents/global-new-search/global-product-search";
import MultiLevelMenu from "@components/store/multi-level-menu";
import CreateShopBanner from "@components/store/navigation/create-shop-banner";
import CreateSwagHeaderRightSection from "@components/create-swag/header/create-swag-header-right-section";
import PreferredColorsDropdown from "@components/create-swag/preferred-colors/dropdown";
import { InternalButton } from "@sharedComponents/buttons";

import useHeaderHeight from "@stadium/ui/src/components/header/use-header-height";
import CheckoutProvider from "@contexts/checkout-context";
import { useStoreContext } from "@components/store/provider";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useCurrentUserContext } from "@contexts/current-user-provider";
import { useWorkspaceContext } from "@contexts/workspace-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { STICKY_HEADER_REF, Header } from "@stadium/ui/src/components/header";

import { getStoreNumber, getIsSinglePageShop } from "@store/selectors/store";

import { SERVER_PHASE, SHOPS } from "@stadium/constants";

import styles from "./main.module.scss";

const DEFAULT_HEADER_HEIGHT = 112;

const WebMultiLevelHeader = ({ isFetchingMenu }) => {
  const storeNumber = useSelector(getStoreNumber);
  const isSinglePageShop = useSelector(getIsSinglePageShop);

  const preferredColorsBodyRef = useRef(null);
  const preferredColorsToggleRef = useRef(null);
  const pathname = usePathname();

  const { isLoading: isLoadingAuth, isAuthenticated } = useAuth();
  const { currentUserData } = useCurrentUserContext();

  const { isActivePackageUser } = useWorkspaceContext();
  const { withMenu, withSearch, withCart, showTalkToSalesCta, hideMenuOnCatalogPage } =
    useStoreContext();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { isNewShopCreationFlow } = useCreateShopContext();

  const headerHeight = useHeaderHeight(DEFAULT_HEADER_HEIGHT);

  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
  const [isSearchToggleActive, setIsSearchToggleActive] = useState(false);

  const showMenu =
    storeNumber && withMenu && !isFetchingMenu && !isSinglePageShop && !hideMenuOnCatalogPage;

  const isLoadingUserProfileNav = isLoadingAuth || (isAuthenticated && !currentUserData?.email);

  const shouldHideElement = isLoadingUserProfileNav || SERVER_PHASE;

  useEffect(() => {
    if (!isColorDropdownOpen) return;
    const handleOutsideClick = (event) => {
      if (
        !preferredColorsBodyRef.current?.contains(event.target) &&
        !preferredColorsToggleRef.current?.contains(event.target)
      ) {
        setIsColorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isColorDropdownOpen]);

  const renderSwagCreationHeader = () => {
    return (
      <>
        <Header
          level={2}
          className={`${styles.topNavBar} ${withMenu ? "" : styles.topNavWithoutMenu}`}
          baseClass=""
          style={{ backgroundColor: "red" }}
        >
          <div
            className={`${styles.navBarWithLogo} ${
              isSwagCreationShopFlow ? styles.swagNavBar : ""
            }`}
          >
            <div className={styles.navContainer}>
              <BrandLogo />
              <div
                className={`${styles.navRightSection} ${
                  shouldHideElement ? styles.hideElement : ""
                }`}
              >
                {withSearch && (
                  <>
                    {isSearchToggleActive ? (
                      <GlobalProductSearch
                        focusInputOnMount
                        key={pathname}
                        searchBlockClass={styles.swagSearchBlock}
                        searchInputClass={styles.swagSearchInput}
                        setIsSearchToggleActive={setIsSearchToggleActive}
                      />
                    ) : (
                      <InternalButton
                        styleType="no-style"
                        className={`${styles.searchIconBtn} ${styles.swagSearchBtn}`}
                        onClick={() => setIsSearchToggleActive(true)}
                        aria-label="Search products"
                      >
                        <i className={styles.searchIcon} />
                      </InternalButton>
                    )}
                  </>
                )}
                <CreateSwagHeaderRightSection
                  isColorDropdownOpen={isColorDropdownOpen}
                  openPreferredColorsDropdown={() => setIsColorDropdownOpen(true)}
                  ref={preferredColorsToggleRef}
                />
              </div>
            </div>
          </div>
        </Header>
        {isColorDropdownOpen && (
          <PreferredColorsDropdown.Body
            closePreferredColorsDropdown={() => setIsColorDropdownOpen(false)}
            ref={preferredColorsBodyRef}
          />
        )}
      </>
    );
  };

  const renderShopHeader = () => {
    if (isSwagCreationShopFlow) {
      return renderSwagCreationHeader();
    }
    return (
      <Header
        level={2}
        className={`${styles.topNavBar} ${withMenu ? "" : styles.topNavWithoutMenu}`}
        baseClass=""
      >
        <AppGenericAnnouncementBar application={SHOPS} options={{ isActivePackageUser }} />
        <div className={styles.navBarWithLogo}>
          <div className={styles.navContainer}>
            <BrandLogo />

            {showTalkToSalesCta && (
              <TalkToSalesButton className={styles.webTalkToSalesBtn} target="_top" />
            )}

            {withSearch && <GlobalProductSearch key={pathname} />}
            <div
              className={`${styles.navRightSection} ${shouldHideElement ? styles.hideElement : ""}`}
            >
              <NavigationRightSection />

              {withCart && (
                <div className={styles.colAuto}>
                  <CheckoutProvider skipPaymentOptions>
                    <CartIcon />
                  </CheckoutProvider>
                </div>
              )}
            </div>
          </div>
        </div>
      </Header>
    );
  };

  return (
    <div style={{ height: `${headerHeight}px` }}>
      <div className={styles.navGroup} ref={STICKY_HEADER_REF}>
        {isNewShopCreationFlow && (
          <Header level={1} baseClass="">
            <CreateShopBanner />
          </Header>
        )}
        {renderShopHeader()}
        {showMenu && (
          <Header level={3} className={styles.headNavigation}>
            <MultiLevelMenu />
          </Header>
        )}
      </div>
    </div>
  );
};

export default WebMultiLevelHeader;
